import {v4 as uuidv4} from "uuid";
import {CSSProperties} from "react";
import {IPixiAppCreateOptions, Application} from "./pixi-app.service.interface";
import {AnimationStatus, IAnimationRenderer, IPixiAnimationPromiseResult} from "./pixi-animation.interface";
import {IDisposableObject} from "../../../utils/disposable-object.interface";
import {PixiAsset} from "./pixi-assets-manager/asset/pixi-asset";
import {IPixiLibWrapper} from "./pixi-lib-wrapper/pixi-lib-wrapper.interface";
import {isOrbitInQAMode} from "../../../utils/qa-mode-utils";

export abstract class PixiAnimationAbstract<T extends string = string, U extends IDisposableObject = PixiAsset> {
    public readonly id = uuidv4();
    private pixiApp: Application<HTMLCanvasElement>;
    private pixiAppDisposer: () => void;
    private _resources: Record<T, U> | null = null;
    private disposeActions: Array<() => void> = [];
    private _signalAborted = false;
    private _status: AnimationStatus = AnimationStatus.Idle;

    constructor(
        private pixiLibWrapperPromise: Promise<IPixiLibWrapper>,
        private pixiAppCreateOptions: IPixiAppCreateOptions,
        private assetsUrls: Record<T, string>,
        private renderer: IAnimationRenderer<T, U>,
        private canvasCss?: CSSProperties,
        private canvasClassName?: string,
        private debugMode?: boolean
    ) {
    }

    private initPromise: Promise<IPixiAnimationPromiseResult> = null;
    public start(): Promise<IPixiAnimationPromiseResult> {
        if (!this.initPromise) {
            this.initPromise = this._initialize();
        }
        return this.initPromise;
    }

    protected abstract onAssetsLoaded(loaderAssets: Record<T, PixiAsset>, pixiLibWrapper: IPixiLibWrapper): Promise<Record<T, U>>;
    protected onBeforeRender(pixiApp: Application<HTMLCanvasElement>, resources: Record<T, U>, pixiLibWrapper: IPixiLibWrapper): void {}

    private async _initialize(): Promise<IPixiAnimationPromiseResult> {
        // The init started, set the status as Loading
        this._status = AnimationStatus.Loading;
        // between constructor and start, the animation could have been aborted or disposed, check it
        if (this._signalAborted) {
            this._status = AnimationStatus.Aborted;
            return {status: AnimationStatus.Aborted};
        }

        // create pixi app. this loads the pixi and pixi-spine libraries first time they are called
        const pixiLibWrapper = await this.pixiLibWrapperPromise;
        let {app: pixiApp, dispose: disposePixiApp, canvas} = pixiLibWrapper.createPixiApplication({
            id: this.id,
            ...this.pixiAppCreateOptions
        });
        if (isOrbitInQAMode() && this.debugMode) {
            /**
             * window.__PIXI_APP__ is set so that we can inspect and debug the animation using this chrome extension
             * https://chromewebstore.google.com/detail/pixijs-devtools/aamddddknhcagpehecnhphigffljadon
             */
            window.__PIXI_APP__ = pixiApp;
        }
        this.pixiApp = pixiApp;
        this.pixiAppDisposer = disposePixiApp;

        this._resources = await pixiLibWrapper.loadAssets(this.assetsUrls).then(loaderAssets => {
            return this.onAssetsLoaded(loaderAssets, pixiLibWrapper);
        })
        .catch((e: unknown) => {
            /* Loading resource can fail if the json is not well formatted according to specs (RFC-8259, ECMA-404) (https://www.json.org/json-en.html , https://en.wikipedia.org/wiki/JSON).
               For example, all members have to be quoted, or else is not a JSON but an object. Ex: NOT OK: {key: "value"} / OK: {"key": "value"}
               In this case the error will be: Uncaught (in promise) Error: [Loader.load] Failed to load json. SyntaxError: Expected property name or '}' in JSON.
               I like to use this validator+formatter: https://elmah.io/tools/json-formatter/ */
            this._status = AnimationStatus.Failed;
            disposePixiApp();
            throw e;
        });
        // This condition is put here for typescript safety. It should never evaluate as true
        if (this._resources === null) {
            this._status = AnimationStatus.Failed;
            this._disposeInternal();
            throw new Error("Pixi Assets were not loaded")
        }

        // loading of pixi libraries and animation resources are async, I must check if someone aborted the animation meanwhile
        if (this._signalAborted) {
            this._status = AnimationStatus.Aborted;
            this._disposeInternal();
            return {status: AnimationStatus.Aborted};
        }

        if (this.canvasClassName) {
            canvas.classList.add(this.canvasClassName);
        }
        // Apply custom CSS to the canvas element created by pixi
        if (this.canvasCss) {
            Object.getOwnPropertyNames(this.canvasCss).forEach((styleKey) => {
                canvas.style[styleKey] = this.canvasCss[styleKey];
            });
        }

        this.onBeforeRender(pixiApp, this._resources, pixiLibWrapper);
        // run the specific animation instructions that are given in constructor
        const renderDisposer = this.renderer.render({pixiApp, resources: this._resources, addDisposeAction: this.addDisposeAction.bind(this), pixiLibWrapper});
        if (renderDisposer) {
            this.disposeActions.push(renderDisposer);
        }

        // all done, set the status to Running and return the status (resolve)
        this._status = AnimationStatus.Running;

        return {status: AnimationStatus.Running, canvas: this.pixiApp.view};
    }

    public attachToDOM(wrapper: HTMLElement): Promise<IPixiAnimationPromiseResult> {
        if (!this.initPromise) {
            throw new Error("Method attachToDOM MUST be called after the method start");
        }
        return this.initPromise.then((result) => {
            // At this point the status can be only Aborted or Running
            // Or, after disposal, the status could be Disposed
            if (result.status === AnimationStatus.Running) {
                wrapper.appendChild(result.canvas);
            }
            return result;
        });
    }

    public get status() {
        return this._status;
    }

    /**
     * Dispose is the one method to be called both for disposal of the running animation and for aborting a loading one
     * If the animation object is newly created or loading, then the controller abort will be called.
     * that will cause a call to _disposeInternal from the _initialize method, as soon as the method checks the aborted flag
     * If the animation is running, we set the disposed status and call _disposeInternal owrselves
     */
    public dispose = () => {
        if (this._status === AnimationStatus.Idle || this._status === AnimationStatus.Loading) {
            this._signalAborted = true;
        } else if (this._status === AnimationStatus.Running) {
            this._status = AnimationStatus.Disposed;
            this._disposeInternal();
        }
    }

    private addDisposeAction(action: () => void) {
        this.disposeActions.push(action);
    }

    private _disposeInternal = () => {
        while (this.disposeActions.length) {
            const disposeAction = this.disposeActions.pop();
            disposeAction();
        };
        if (this.renderer.dispose) {
            this.renderer.dispose();
        }
        const assets = Object.keys(this._resources);
        while (assets.length) {
            const resource = assets.pop();
            this._resources[resource].dispose();
        }
        this.pixiAppDisposer();
    }

}
